<template>
  <label
    :class="{
      'is-checked': checked,
      'is-indeterminate': isIndeterminate,
      'is-static': isStatic,
      'is-dark': dark,
    }"
    :style="{
      '--size': `${size}px`,
    }"
    class="checkbox"
  >
    <input
      v-if="!isStatic"
      :id="id"
      :key="id"
      ref="input"
      type="checkbox"
      :checked="checked"
      :name="id"
      :disabled="disabled"
      :indeterminate.prop="isIndeterminate"
      @change="$emit('change', $event.target.checked)"
    >
    <span
      class="checkbox__icon"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        width="13"
        height="13"
        viewBox="0 0 26 26"
      >
        <image
          width="26"
          height="26"
          xlink:href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA0AAAANCAMAAABFNRROAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAZlBMVEWAgICPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvWPtvUAAAA5lW/5AAAAIHRSTlMAhOXmj+i6WuvXM7fu0E3k6hwG0k7nDCq8TPUH6ZKNmzPkfPoAAAABYktHRCHEbA0WAAAACXBIWXMAAAsSAAALEgHS3X78AAAAB3RJTUUH5AUIDRsmP7aJnwAAAFdJREFUCNddjkcWgCAQQyOI2LFhL3P/U8oCcJ5Z5S9SkAgZlYK4oBhIyGAzTTmEhwKlI0VV7aBBS2TQUT8QjZh8zs7Lii227Ac06zyvsMD37O/Lbb6fzwseHA7Ec6nJTgAAAABJRU5ErkJggg=="
        />
      </svg>
    </span>
    <span
      v-if="$slots.default || (title && title.length > 0)"
      class="checkbox__title"
      :style="{
        'padding-left': `${size / 2}px`,
      }"
    ><slot>{{ title }}</slot>
    </span>
  </label>
</template>

<script>
export default {
  name: 'checkbox',
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    dark: {
      default: false,
      type: Boolean,
    },
    indeterminate: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    title: {
      default: '',
      type: String,
    },
    name: {
      type: String,
      required: true,
    },
    checked: {
      type: Boolean,
    },
    isStatic: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    scope: {
      type: String,
      default: 'form-scope',
    },
    size: {
      default: 14,
      type: [Number, String],
    },
  },
  data() {
    return { isIndeterminate: this.indeterminate };
  },
  computed: {
    id() {
      return `${this.scope}-${this.name.toLowerCase()}`;
    },
  },
  watch: {
    checked() {
      this.isIndeterminate = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.checkbox {
  cursor: pointer;
  --default-size: 14px;
  --icon-lh-ratio: 20 / 14;
  --lh: calc(var(--icon-lh-ratio) * var(--size, --default-size));

  padding: 0 calc(var(--size, --default-size) * (5 / 20));
  .no-touchevents &:hover {
    background: rgba(#fff, 0.05);
  }
  &:active {
    background: rgba(#fff, 0.05);
  }

  $clr-dark: #4c4c7f;
  $clr-light: #8fb6f5;
  $clr-white: #fff;
  $clr-black: #333;

  display: inline-flex;
  align-items: flex-start;
  font-size: var(--size, --default-size);
  line-height: var(--lh);
  user-select: none;

  input {
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
  }

  &.is-checked &__icon,
  input:checked + &__icon {
    svg {
      opacity: 1;
    }
  }

  &.is-checked &__title,
  input:checked ~ &__title,
  .no-touchevents &:hover &__title,
  &:active &__title,
  input:focus ~ &__title {
    color: $clr-white;
  }

  &.is-dark.is-checked &__title,
  &.is-dark input:checked ~ &__title {
    color: $clr-dark;
  }

  &.is-indeterminate &__icon,
  input:indeterminate + &__icon {
    background: #ccc;
    border-color: #ccc;

    &:before {
      opacity: 1;
    }
  }

  &.is-disabled &__icon,
  input:disabled + &__icon {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &__icon {
    cursor: pointer;
    flex: 0 0 var(--size, --default-size);
    width: var(--size, --default-size);
    height: var(--size, --default-size);
    position: relative;
    background-color: $clr-dark;
    border-radius: calc(var(--size, --default-size) * 0.2);
    overflow: hidden;
    margin-top: calc((var(--lh) - var(--size, --default-size)) / 2);

    svg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      transition: opacity 0.15s ease-in-out;
      fill: $clr-light;
      opacity: 0;
    }

    &:before {
      content: '';
      position: absolute;
      opacity: 0;
      transition: opacity 0.15s ease-in-out;
      top: 50%;
      left: 50%;
      width: 75%;
      height: 20%;
      background-color: #fff;
      transform: translate(-50%, -50%);
    }
  }

  &__title {
    flex-grow: 1;
    cursor: pointer;
    padding-left: calc(var(--size, --default-size) / 2);
    font-weight: 500;
    color: #b4b4d3;
  }

  &.is-static &__icon,
  &.is-static &__title {
    cursor: default;
  }
}
</style>
